import { safeSessionStorage } from '@toss/storage';
import { atom } from 'jotai';

import { EMPTY_NAV_PATH_STATE, type NavPathState } from '../utils/navPath';

/** 걸어온 길은 새로고침해도 이어지도록 탭 단위 sessionStorage 에 남긴다. */
const STORAGE_KEY = 'quiz-navigation-path';

function readStoredNavPath(): NavPathState {
  try {
    const raw = safeSessionStorage.get(STORAGE_KEY);
    if (!raw) return EMPTY_NAV_PATH_STATE;
    const parsed = JSON.parse(raw) as Partial<NavPathState>;
    return {
      paths: parsed.paths ?? {},
      order: parsed.order ?? [],
      labels: parsed.labels ?? {},
      // 지난 문서의 마지막 항목은 다시 열기 판정용으로 따로 둔다(`entryKey`).
      currentKey: null,
      lastShownKey: parsed.currentKey ?? null,
    };
  } catch {
    return EMPTY_NAV_PATH_STATE;
  }
}

/** `null` = 아직 저장값을 읽지 않음. 구독하면 저장값을 불러온다. */
const rawNavPathAtom = atom<NavPathState | null>(null);
rawNavPathAtom.onMount = set => {
  set(prev => prev ?? readStoredNavPath());
};

/** 읽기 전용. 쓰기는 `updateNavPathAtom` 으로만 한다. */
export const navPathAtom = atom(
  get => get(rawNavPathAtom) ?? EMPTY_NAV_PATH_STATE,
);

/**
 * 항상 저장값 위에 쓴다. `navPathAtom` 은 "아직 안 읽음"을 빈 길로 보여주므로 `rawNavPathAtom` 을 읽는다
 * — 빈 길 위에 쓰면 탭 기록이 지워진다.
 */
export const updateNavPathAtom = atom(
  null,
  (get, set, update: (prev: NavPathState) => NavPathState) => {
    const loaded = get(rawNavPathAtom);
    const base = loaded ?? readStoredNavPath();
    const next = update(base);
    if (next !== base)
      safeSessionStorage.set(STORAGE_KEY, JSON.stringify(next));
    if (next !== loaded) set(rawNavPathAtom, next);
  },
);
